import { Ionicons } from '@expo/vector-icons';
import React, { useState } from 'react';
import { View } from 'react-native';

import { useNotification } from '@/components/notification-system';
import { ActivityIndicator, Button, Text, useTheme } from '@/components/ui';
import { useApplyMessageContacts, useMessageContactPlan } from '@/hooks/use-data';
import type { PlannedContact } from '@/services/data-service';
import { friendlyApiMessage } from '@/utils/api-error';

const ROLE_LABEL: Record<PlannedContact['role'], string> = {
	owner: 'Owner',
	emergency_contact: 'Emergency contact',
};

/**
 * Owner Questionnaire → contacts on the lot (#369).
 *
 * A PREVIEW with an explicit apply, never automatic. This is the only inbox action that
 * writes a lot's ownership, from an unverified public web form — a wrong or duplicated
 * owner affects billing and mailings and is a mess to unpick. Preview and apply share one
 * server-side planner, so what is shown is what happens.
 *
 * The warnings get as much room as the plan. They are the interesting part: an
 * unattributable phone number, or a legacy combined "Kenneth & Tina Ball" record that
 * applying would leave sitting beside the two real people.
 */
export const ContactPlanPanel: React.FC<{ messageId: string; canApply: boolean }> = ({ messageId, canApply }) => {
	const theme = useTheme();
	const { showToast, showModal } = useNotification();
	const { data: plan, isLoading } = useMessageContactPlan(messageId);
	const apply = useApplyMessageContacts();
	const [applied, setApplied] = useState(false);

	if (isLoading) {
		return (
			<View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 16 }}>
				<ActivityIndicator size="small" />
				<Text style={{ marginLeft: 8, color: theme.colors.onSurfaceVariant }}>Reading contact details…</Text>
			</View>
		);
	}
	if (!plan || plan.people.length === 0) return null;

	const onApply = async () => {
		try {
			const result = await apply.mutateAsync(messageId);
			setApplied(true);
			showToast(`${result.created} created, ${result.updated} updated`, { variant: 'success' });
		} catch (error) {
			showModal({
				title: 'Could not save these contacts',
				message: friendlyApiMessage(error, 'Failed to apply the contacts.'),
				variant: 'error',
			});
		}
	};

	return (
		<View
			style={{
				marginTop: 14,
				padding: 12,
				borderRadius: 8,
				borderWidth: 1,
				borderColor: theme.colors.outlineVariant ?? theme.colors.outline,
				backgroundColor: theme.colors.surfaceVariant,
			}}
			testID="contact-plan-panel"
		>
			<View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 8 }}>
				<Ionicons name="people-outline" size={16} color={theme.colors.primary} style={{ marginRight: 6 }} />
				<Text style={{ fontSize: 12, fontWeight: '700', letterSpacing: 0.4, color: theme.colors.onSurfaceVariant }}>
					CONTACTS ON THIS FORM{plan.accountNumber ? ` · ${plan.accountNumber}` : ''}
				</Text>
			</View>

			{plan.people.map((person) => (
				<View
					key={`${person.role}-${person.firstName}-${person.lastName ?? ''}`}
					style={{ paddingVertical: 6, borderTopWidth: 1, borderTopColor: theme.colors.outlineVariant ?? theme.colors.outline }}
				>
					<View style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap' }}>
						<Text style={{ fontWeight: '600', marginRight: 6 }}>
							{person.firstName} {person.lastName ?? ''}
						</Text>
						<Text style={{ fontSize: 11, color: theme.colors.onSurfaceVariant }}>
							{ROLE_LABEL[person.role]}{person.relationship ? ` · ${person.relationship}` : ''}
						</Text>
						{/* Says plainly whether this ADDS a record or edits one already there —
							 the difference between a tidy update and an accidental duplicate. */}
						<Text
							style={{
								fontSize: 11,
								fontWeight: '700',
								marginLeft: 8,
								color: person.action === 'create' ? theme.colors.primary : theme.colors.onSurfaceVariant,
							}}
						>
							{person.sameAsOwner
								? 'SAME PERSON AS OWNER'
								: person.action === 'create' ? 'NEW' : `UPDATES EXISTING${person.matchedBy ? ` (${person.matchedBy})` : ''}`}
						</Text>
						{person.retiresOwnerLink ? (
							<Text style={{ fontSize: 11, fontWeight: '700', marginLeft: 8, color: theme.colors.error }}>
								NO LONGER AN OWNER
							</Text>
						) : null}
					</View>
					{person.email || person.phone ? (
						<Text style={{ color: theme.colors.onSurfaceVariant }}>
							{[person.email, person.phone].filter(Boolean).join(' · ')}
						</Text>
					) : null}
				</View>
			))}

			{plan.warnings.length > 0 ? (
				<View style={{ marginTop: 10 }}>
					{plan.warnings.map((warning) => (
						<View key={warning} style={{ flexDirection: 'row', marginTop: 4 }}>
							<Ionicons name="alert-circle-outline" size={14} color={theme.colors.error} style={{ marginRight: 6, marginTop: 2 }} />
							<Text style={{ flex: 1, minWidth: 0, fontSize: 12, color: theme.colors.error }}>{warning}</Text>
						</View>
					))}
				</View>
			) : null}

			{canApply && plan.accountId ? (
				<View style={{ flexDirection: 'row', marginTop: 12 }}>
					<Button
						mode="contained"
						icon="person-add-outline"
						onPress={onApply}
						disabled={apply.isPending}
						loading={apply.isPending}
						testID="btn-apply-contacts"
					>
						{applied ? 'Re-apply to lot' : 'Update owners'}
					</Button>
				</View>
			) : null}
		</View>
	);
};

export default ContactPlanPanel;
